import React, { useState, useEffect, useCallback } from "react";
import { QRCodeSVG } from "qrcode.react";
import Sidebar from "../Sidebar";
import Header from "../Header";
import "./Devices.css";
import axios from "axios";
import API_BASE_URL from "../../config";
import {
  BsDeviceSsd,
  BsTrash3Fill,
  BsCheckCircleFill,
  BsXCircleFill,
  BsExclamationTriangleFill,
  BsClipboard,
  BsCheck2,
} from "react-icons/bs";

export default function Devices() {
  const [devices, setDevices] = useState([]);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [pairing, setPairing] = useState({ loading: false, pairUrl: "", error: "" });
  const [copiedId, setCopiedId] = useState(null);
  const API_URL = `${API_BASE_URL}/device`;

  const handleCopy = (id) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getUserToken = () => localStorage.getItem("userToken");

  useEffect(() => {
    if (!getUserToken()) window.location.href = "/login";
  }, []);

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    const userToken = getUserToken();
    if (!userToken) return;
    try {
      const headers = { Authorization: `Bearer ${userToken}` };
      const response = await axios.get(`${API_URL}/list`, { headers });
      if (response.status === 200) setDevices(response.data.devices || []);
    } catch (error) {
      console.error("Error fetching devices:", error);
      showToast("error", "Failed to load devices.");
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => { fetchDevices(); }, [fetchDevices]);

  const startPairing = useCallback(async () => {
    const token = getUserToken();
    if (!token) return;
    setPairing((p) => ({ ...p, loading: true, error: "" }));
    try {
      const { data: res } = await axios.post(`${API_BASE_URL}/device/pairing/start`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setPairing({ loading: false, pairUrl: res.pairUrl, error: "" });
    } catch (err) {
      const msg = err.response?.status === 402
        ? "Activate the SMS service to link a phone."
        : (err.response?.data?.error || "Could not start pairing.");
      setPairing({ loading: false, pairUrl: "", error: msg });
    }
  }, [API_URL]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { startPairing(); }, [startPairing]);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  };

  const handleDelete = async () => {
    if (!deviceToDelete) return;
    const userToken = getUserToken();
    if (!userToken) return;
    try {
      const headers = { Authorization: `Bearer ${userToken}` };
      const response = await axios.delete(`${API_URL}/delete/${deviceToDelete}`, { headers });
      if (response.status === 200) {
        showToast("success", "Device removed.");
        setDevices(devices.filter((d) => d.device_fingerprint !== deviceToDelete));
      }
    } catch (error) {
      console.error("Error deleting device:", error);
      showToast("error", "Failed to remove device.");
    }
    setDeviceToDelete(null);
  };

  return (
    <div className="page-container">
      <Sidebar />
      <div className="main-content">
        <Header />
        <div className="container mt-4">
          <div className="page-header dash-head">
            <div>
              <h4>Android devices</h4>
              <p className="page-subtitle">Pair the Pairfon app on an Android phone to send &amp; receive SMS.</p>
            </div>
            <button className="dash-cta" onClick={startPairing} disabled={pairing.loading}>
              {pairing.loading ? "Generating…" : "＋ Pair device"}
            </button>
          </div>

          {loading ? (
            <div className="page-loading"><div className="spinner" /><p>Loading devices…</p></div>
          ) : (
            <div className="dev-grid2">
              <div className="dev-card">
                <div className="dev-card-h"><h6>Connected devices</h6></div>
                {devices.length === 0 ? (
                  <div className="dev-empty">
                    <BsDeviceSsd size={30} />
                    <div><strong>No devices paired yet</strong><p>Scan the QR to pair your first phone.</p></div>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="dev-table">
                      <thead><tr><th>Device</th><th>SIMs</th><th>Last seen</th><th>Status</th><th></th></tr></thead>
                      <tbody>
                        {devices.map((d) => {
                          const online = d.status === "active";
                          return (
                            <tr key={d.device_fingerprint}>
                              <td>
                                <div className="dev-nm">{d.device_name || "Unnamed device"}</div>
                                <div className="dev-fp">
                                  <code>{d.device_fingerprint}</code>
                                  <button className="dev-copy" onClick={() => handleCopy(d.device_fingerprint)} title="Copy device ID">
                                    {copiedId === d.device_fingerprint ? <BsCheck2 /> : <BsClipboard />}
                                  </button>
                                </div>
                              </td>
                              <td>{d.sim_slot || "—"}</td>
                              <td className="dev-mut">{d.last_heartbeat ? new Date(d.last_heartbeat).toLocaleString() : "—"}</td>
                              <td><span className={`dev-tag ${online ? "on" : "off"}`}>{online ? "Online" : "Offline"}</span></td>
                              <td className="dev-actions">
                                <button className="dev-del" onClick={() => setDeviceToDelete(d.device_fingerprint)} title="Remove device"><BsTrash3Fill /></button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="dev-card">
                <div className="dev-card-h"><h6>Pair a new device</h6></div>
                <div className="dev-pair">
                  <div className="dev-qr">
                    {pairing.pairUrl
                      ? <QRCodeSVG value={pairing.pairUrl} size={148} bgColor="#ffffff" fgColor="#0a0c12" />
                      : <span className="dev-qr-ph">{pairing.loading ? "…" : "QR"}</span>}
                  </div>
                  <div className="dev-steps">
                    <div className="dev-step"><span className="n">1</span><div>Install the <b>Pairfon</b> app from Google Play.</div></div>
                    <div className="dev-step"><span className="n">2</span><div>Open the app and tap <b>Scan to pair</b>.</div></div>
                    <div className="dev-step"><span className="n">3</span><div>Point it at this QR — it links in seconds.</div></div>
                    {pairing.error && <div className="dev-err">{pairing.error}</div>}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {deviceToDelete && (
        <div className="modal-overlay" onClick={() => setDeviceToDelete(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-icon-wrap modal-icon-danger"><BsExclamationTriangleFill /></div>
            <h5>Remove device?</h5>
            <p>This will unpair the device and remove its data. This can't be undone.</p>
            <code className="modal-device-id">{deviceToDelete}</code>
            <div className="modal-actions">
              <button className="btn-modal-cancel" onClick={() => setDeviceToDelete(null)}>Cancel</button>
              <button className="btn-modal-danger" onClick={handleDelete}>Remove</button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`toast-notification ${toast.type === "success" ? "toast-success" : "toast-error"}`}>
          {toast.type === "success" ? <BsCheckCircleFill /> : <BsXCircleFill />}
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
}
